import React, { useState, useEffect } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const ContractorPermitList = () => {
    const [view, setView] = useState('list'); 
    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form Data
    const [projects, setProjects] = useState([]);
    const [availablePermits, setAvailablePermits] = useState([]);
    const [permitData, setPermitData] = useState({}); 
    
    // FIX 1: Default the date to today to prevent backend validation errors
    const [formData, setFormData] = useState({ 
        project: '', 
        date: new Date().toISOString().split('T')[0], 
        location: '' 
    });

    useEffect(() => { loadPermits(); loadConfig(); }, []);

    const loadPermits = async () => {
        try { const res = await api.get('approvals/mine/'); setPermits(res.data); setLoading(false); } 
        catch (err) { console.error(err); setLoading(false); }
    };
    const loadConfig = async () => {
        try { const [pRes, tRes] = await Promise.all([api.get('config/projects/'), api.get('config/permits/')]); setProjects(pRes.data); setAvailablePermits(tRes.data); } 
        catch (err) { console.error(err); }
    };

    const handleDownloadPDF = async (permitId) => {
        try {
            const response = await api.get(`permits/${permitId}/pdf/`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Permit_${permitId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) { 
            console.error("PDF Error", err);
            alert("Error downloading PDF"); 
        }
    };

    // --- FORM HANDLERS ---
    const handleTogglePermit = (id) => {
        setPermitData(prev => {
            const newData = { ...prev };
            if (newData[id]) delete newData[id]; 
            else newData[id] = { notes: '', answers: {} }; 
            return newData;
        });
    };

    const handleAnswerChange = (pId, qIdx, value) => {
        setPermitData(prev => ({
            ...prev, [pId]: { ...prev[pId], answers: { ...prev[pId].answers, [qIdx]: value } }
        }));
    };

    const handleNotesChange = (pId, text) => {
        setPermitData(prev => ({
            ...prev, [pId]: { ...prev[pId], notes: text }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // FIX 2: Add Validation before submitting
        if (!formData.project) return alert("⚠️ Please select a Project.");
        if (!formData.date) return alert("⚠️ Please select a Date.");
        if (Object.keys(permitData).length === 0) return alert("⚠️ Please select at least one permit type.");

        const requests = Object.keys(permitData).map(async (permitId) => {
            const data = permitData[permitId];
            const type = availablePermits.find(p => p.id === parseInt(permitId));
            
            let desc = "--- CHECKLIST ---\n";
            if (type && type.questions) {
                type.questions.forEach((q, idx) => {
                    const qText = typeof q === 'object' ? q.text : q;
                    const ansValue = data.answers[idx];
                    
                    // Format answer for display
                    let displayAns = "❌ NO";
                    if (ansValue === true || ansValue === 'Yes') displayAns = "✅ YES";
                    else if (ansValue === false || ansValue === 'No') displayAns = "❌ NO";
                    else if (ansValue && typeof ansValue === 'string') displayAns = ansValue; 
                    
                    desc += `${qText}: ${displayAns}\n`;
                });
            }
            if (data.notes) desc += `\n--- NOTES ---\n${data.notes}`;

            return api.post('permits/submit/', {
                project: formData.project, 
                date: formData.date, 
                location: formData.location, 
                type_id: parseInt(permitId), 
                details: desc
            });
        });

        try {
            await Promise.all(requests);
            alert("✅ Permits Submitted!");
            setView('list'); 
            setPermitData({}); 
            setFormData(p => ({...p, location: ''})); 
            loadPermits();
        } catch (err) { 
            // FIX 3: Improve Error Logging
            console.error("Submit Error:", err);
            const msg = err.response?.data?.detail || err.response?.data?.message || "Error submitting permits.";
            alert(`❌ Error: ${msg}`); 
        }
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', boxSizing:'border-box', marginBottom:'10px' };

    // --- LOGIC: RENDER QUESTIONS ---
    const renderQuestions = (type) => {
        if (!type.questions) return null;

        return type.questions.map((q, idx) => {
            const qText = typeof q === 'object' ? q.text : q;
            const qType = typeof q === 'object' ? (q.type || 'checkbox') : 'checkbox';
            
            // Logic Jump Checks
            const showIfId = typeof q === 'object' ? q.showIf : null;
            const showIfCond = (typeof q === 'object' ? q.showIfCondition : 'Yes') || 'Yes'; 

            if (showIfId) {
                const parentIdx = type.questions.findIndex(pq => (typeof pq === 'object' ? pq.id : null) === showIfId);
                const parentAnswer = permitData[type.id]?.answers?.[parentIdx];

                // Normalize answer (legacy boolean vs new string)
                const normalizedAnswer = (parentAnswer === true) ? 'Yes' : (parentAnswer === false ? 'No' : parentAnswer);
                
                if (normalizedAnswer !== showIfCond) return null;
            }

            return (
                <div key={idx} style={{marginBottom:'15px', paddingLeft: showIfId ? '15px' : '0', borderLeft: showIfId ? `2px solid ${THEME.border}` : 'none'}}>
                    {qType === 'text' ? (
                        <div>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.85rem', marginBottom:'4px'}}>{qText}</label>
                            <input 
                                type="text"
                                value={permitData[type.id]?.answers?.[idx] || ''}
                                onChange={(e) => handleAnswerChange(type.id, idx, e.target.value)}
                                style={{...inputStyle, padding:'8px', marginBottom:0}}
                                placeholder="Enter details..."
                            />
                        </div>
                    ) : (
                        // Yes/No Buttons
                        <div>
                            <label style={{display:'block', color:'white', fontSize:'0.9rem', marginBottom:'6px'}}>{qText}</label>
                            <div style={{display:'flex', gap:'10px'}}>
                                {['Yes', 'No'].map(opt => {
                                    const isSelected = permitData[type.id]?.answers?.[idx] === opt;
                                    const activeColor = opt === 'Yes' ? THEME.success : THEME.danger;

                                    return (
                                        <div 
                                            key={opt}
                                            onClick={() => handleAnswerChange(type.id, idx, opt)}
                                            style={{
                                                flex: 1, 
                                                textAlign: 'center', 
                                                padding: '8px', 
                                                cursor: 'pointer', 
                                                fontSize: '0.85rem', 
                                                borderRadius: '4px',
                                                border: `1px solid ${isSelected ? activeColor : THEME.border}`,
                                                background: isSelected ? `${activeColor}33` : 'transparent',
                                                color: isSelected ? 'white' : THEME.textSec,
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {opt}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        });
    };

    if (loading) return <div style={{color:'white'}}>Loading...</div>;

    return (
        <div className="animate-fade-in">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <h2 style={{color:'white', margin:0}}>🪪 My Permits</h2>
                {view === 'list' ? (
                    <button onClick={() => setView('new')} style={{padding:'8px 16px', background: THEME.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer'}}>➕ Request Permit</button>
                ) : (
                    <button onClick={() => setView('list')} style={{padding:'8px 16px', background: 'transparent', color: THEME.textSec, border:`1px solid ${THEME.border}`, borderRadius:'6px', cursor:'pointer'}}>Cancel</button>
                )}
            </div>

            {view === 'list' && (
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px'}}>
                    {permits.map(p => (
                        <div key={p.id} style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, position:'relative'}}>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'start'}}>
                                <div>
                                    <div style={{fontWeight:'bold', color:'white', fontSize:'1.1rem'}}>{p.permit_type_details?.name}</div>
                                    <div style={{fontSize:'0.85rem', color: THEME.textSec}}>{p.project_name} • {p.location_label}</div>
                                </div>
                                <span style={{padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem', fontWeight:'bold', background: p.status === 'APPROVED' ? `rgba(16,185,129,0.2)` : `rgba(250,204,21,0.2)`, color: p.status === 'APPROVED' ? THEME.success : THEME.warning}}>
                                    {p.status}
                                </span>
                            </div>
                            <div style={{marginTop:'15px', paddingTop:'15px', borderTop:`1px dashed ${THEME.border}`, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <span style={{color:THEME.textSec, fontSize:'0.8rem'}}>{p.date_label}</span>
                                {p.status === 'APPROVED' && <button onClick={() => handleDownloadPDF(p.id)} style={{background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'4px', cursor:'pointer', padding:'4px 10px'}}>📄 PDF</button>}
                            </div>
                        </div>
                    ))}
                    {permits.length === 0 && <div style={{color:THEME.textSec, textAlign:'center', gridColumn:'1/-1'}}>No permits found.</div>}
                </div>
            )}

            {view === 'new' && (
                <div className="card" style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, maxWidth:'800px', margin:'0 auto'}}>
                    <form onSubmit={handleSubmit}>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
                            <select value={formData.project} onChange={e => setFormData({...formData, project: e.target.value})} style={inputStyle}>
                                <option value="">Select Project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={inputStyle} />
                        </div>
                        <input placeholder="Specific Location" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
                        
                        <h4 style={{color:THEME.primary, marginBottom:'10px'}}>Select Permits</h4>
                        <div style={{display:'grid', gap:'10px', marginBottom:'20px'}}>
                            {availablePermits.map(type => {
                                const isSel = !!permitData[type.id];
                                return (
                                    <div key={type.id} style={{background: isSel ? 'rgba(59,130,246,0.1)' : '#020617', padding:'15px', borderRadius:'6px', border:`1px solid ${isSel ? THEME.primary : THEME.border}`}}>
                                        <label style={{color:'white', cursor:'pointer', display:'flex', alignItems:'center', fontWeight:'bold'}}>
                                            <input type="checkbox" checked={isSel} onChange={() => handleTogglePermit(type.id)} style={{marginRight:'10px', width:'18px', height:'18px'}} />
                                            {type.name}
                                        </label>
                                        
                                        {isSel && (
                                            <div style={{marginTop:'15px', paddingLeft:'28px'}}>
                                                {renderQuestions(type)}
                                                
                                                <div style={{marginTop:'15px'}}>
                                                    <label style={{display:'block', color:THEME.textSec, fontSize:'0.75rem', marginBottom:'5px', textTransform:'uppercase'}}>Additional Notes</label>
                                                    <textarea 
                                                        rows="2" 
                                                        placeholder="Notes..." 
                                                        value={permitData[type.id].notes || ''} 
                                                        onChange={e => handleNotesChange(type.id, e.target.value)} 
                                                        style={{width:'100%', padding:'8px', borderRadius:'4px', border:`1px solid ${THEME.border}`, background:'#0f172a', color:'white', fontSize:'0.85rem'}} 
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <button type="submit" style={{width:'100%', padding:'12px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Submit Application</button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ContractorPermitList;